import { useEffect, useState } from 'react';

/** True when the visitor asked the OS for reduced motion. Updates live. */
export function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);
  return reduced;
}

/**
 * Enables the motion system on the page root and reveals [data-reveal]
 * elements once as they enter the viewport. Without IntersectionObserver,
 * or with reduced motion, content renders in its final state.
 */
export function useRevealRoot(rootRef, reduced) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const canAnimate = !reduced && 'IntersectionObserver' in window;
    root.classList.toggle('motion-ok', canAnimate);
    root.classList.toggle('is-static', !canAnimate);
    if (!canAnimate) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    root.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef, reduced]);
}

/* One shared event lets any CTA open the floating call form. */
export const OPEN_CALL_EVENT = 'a9:open-call';
export function openCallForm(source) {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_CALL_EVENT, { detail: { source } }));
  } catch (e) {
    /* never block a click */
  }
}
